import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@shared/ui/button/Button';
import { Input } from '@shared/ui/input/Input';
import { Select } from '@shared/ui/select/Select';
import { receiptFormSchema } from '../schemas/receiptSchema';
import type { ReceiptFormInput, ReceiptFormValues } from '../schemas/receiptSchema';
import { DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS, RECEIPT_CATEGORIES } from '../types';
import type { ReceiptExtraction, ReceiptExtractionField } from '../types';
import { ConfidenceBadge } from './ConfidenceBadge';

export interface ReceiptFormProps {
  /** Données extraites par l'IA, préremplies mais entièrement éditables. */
  extraction: ReceiptExtraction;
  onSubmit: (values: ReceiptFormValues) => void;
  onCancel: () => void;
  /** Désactive le bouton pendant la persistance (appel API). */
  submitting?: boolean;
}

const categoryOptions = RECEIPT_CATEGORIES.map((category) => ({
  value: category,
  label: category,
}));

const documentTypeOptions = DOCUMENT_TYPES.map((type) => ({
  value: type,
  label: DOCUMENT_TYPE_LABELS[type],
}));

function toFormValues(extraction: ReceiptExtraction): ReceiptFormInput {
  return {
    ...extraction,
    total: extraction.unrecognizedFields?.includes('total') ? '' : extraction.total,
    vat: extraction.unrecognizedFields?.includes('vat') ? '' : extraction.vat,
  };
}

/**
 * Formulaire de revue des données extraites.
 * React Hook Form + Zod : les valeurs IA sont préremplies, chaque champ
 * est éditable et validé avant la persistance.
 */
export function ReceiptForm({
  extraction,
  onSubmit,
  onCancel,
  submitting = false,
}: ReceiptFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<ReceiptFormInput, unknown, ReceiptFormValues>({
    resolver: zodResolver(receiptFormSchema),
    defaultValues: toFormValues(extraction),
  });

  // Une nouvelle extraction (nouvelle analyse) réinitialise le formulaire.
  useEffect(() => {
    reset(toFormValues(extraction));
  }, [extraction, reset]);

  const confidence = watch('confidence');
  const manualWarning = (field: ReceiptExtractionField): string | undefined =>
    extraction.unrecognizedFields?.includes(field) && !dirtyFields[field]
      ? "Non reconnu par l'IA — saisie manuelle requise."
      : undefined;
  const remainingFields =
    extraction.unrecognizedFields?.filter((field) => !dirtyFields[field]).length ?? 0;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-5 rounded-lg border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      aria-label="Données extraites du document"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <h2 className="text-sm font-semibold text-slate-900">Données extraites</h2>
        <p className="text-xs text-slate-500">
          Proposées par l'IA à titre d'aide : vérifiez et corrigez chaque champ avant de valider.
        </p>
        <ConfidenceBadge confidence={confidence} />
        {remainingFields > 0 ? (
          <p
            role="status"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            {remainingFields} information{remainingFields > 1 ? 's' : ''} non reconnue
            {remainingFields > 1 ? 's' : ''} : complétez les champs signalés avant de valider.
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Commerçant"
          error={errors.merchant?.message}
          warning={manualWarning('merchant')}
          {...register('merchant')}
        />
        <Input
          label="Date"
          type="date"
          error={errors.date?.message}
          warning={manualWarning('date')}
          {...register('date')}
          min="1900-01-01"
          max={new Date().toISOString().split('T')[0]}
        />
        <Input
          label="Montant TTC"
          type="number"
          step="0.01"
          min="0"
          error={errors.total?.message}
          warning={manualWarning('total')}
          {...register('total')}
        />
        <Input
          label="TVA"
          type="number"
          step="0.01"
          min="0"
          error={errors.vat?.message}
          warning={manualWarning('vat')}
          {...register('vat')}
        />
        <Input
          label="Devise"
          maxLength={3}
          error={errors.currency?.message}
          warning={manualWarning('currency')}
          {...register('currency')}
        />
        <Select
          label="Catégorie"
          options={categoryOptions}
          error={errors.category?.message}
          warning={manualWarning('category')}
          {...register('category')}
        />
        <Select
          label="Type de document"
          options={documentTypeOptions}
          error={errors.documentType?.message}
          warning={manualWarning('documentType')}
          {...register('documentType')}
        />
      </div>

      <div className="flex items-center gap-3 border-t border-slate-100 pt-4">
        <Button type="submit" disabled={isSubmitting || submitting}>
          {submitting ? 'Enregistrement…' : 'Valider le ticket'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
